import type { LocalizedString } from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { GripVertical, Pencil, Trash2 } from "lucide-react";
import { useState, useTransition } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LocalizedInput } from "@/components/ui/localized-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useConfirm } from "@/hooks/use-confirm";
import { del, patch, post } from "@/lib/api/_client";
import { useClientTabs } from "@/lib/hooks/use-clients";
import { useUserLanguage } from "@/lib/i18n";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import type { ClientTab, CustomField } from "../settings-types";
import { T } from "./client-tabs-wrapper.i18n";

type ClientTabsWrapperProps = {
  initialFields: CustomField[];
  supportedLanguages: string[];
};

export function ClientTabsWrapper({ initialFields, supportedLanguages }: ClientTabsWrapperProps) {
  const { userLanguage } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const { clientTabs } = useClientTabs();
  const queryClient = useQueryClient();
  const [fields] = useState(initialFields);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTab, setEditingTab] = useState<ClientTab | null>(null);
  const [tabName, setTabName] = useState("");
  const [nameI18n, setNameI18n] = useState<LocalizedString>({});
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragTabs, setDragTabs] = useState<ClientTab[] | null>(null);
  const { confirm, ConfirmationDialog } = useConfirm();

  // Use drag state during drag, otherwise use hook data
  const tabs = dragTabs ?? clientTabs;

  const invalidateTabs = () => queryClient.invalidateQueries({ queryKey: ["clientTabs"] });

  const handleCreate = () => {
    setEditingTab(null);
    setTabName("");
    setNameI18n({});
    setError(null);
    setDialogOpen(true);
  };

  const handleEdit = (tab: ClientTab) => {
    setEditingTab(tab);
    // Extract user's language value from nameI18n
    const i18n = tab.nameI18n || {};
    const nameInUserLang = i18n[userLanguage] || Object.values(i18n)[0] || "";
    // Store other translations separately
    const otherTranslations = Object.fromEntries(
      Object.entries(i18n).filter(([lang]) => lang !== userLanguage),
    );
    setTabName(nameInUserLang);
    setNameI18n(otherTranslations);
    setError(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    startTransition(async () => {
      try {
        const url = editingTab ? `/internal/client-tabs/${editingTab.id}` : "/internal/client-tabs";
        if (editingTab) {
          await patch(url, { name: tabName, nameI18n, userLanguage });
        } else {
          await post(url, { name: tabName, nameI18n, userLanguage });
        }

        invalidateTabs();
        setDialogOpen(false);
      } catch (err: any) {
        setError(err.message || t.somethingWentWrong);
      }
    });
  };

  const handleDelete = async (id: string, name: string) => {
    const confirmed = await confirm({
      title: t.deleteTab,
      description: t.deleteTabConfirmation(name),
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) return;

    startTransition(async () => {
      try {
        await del(`/internal/client-tabs/${id}`);
        invalidateTabs();
      } catch (err: any) {
        await confirm({
          title: t.error,
          description: err.message || t.failedToDelete,
          confirmText: t.ok,
          variant: "destructive",
          type: "alert",
        });
      }
    });
  };

  const handleDragStart = (index: number) => {
    setDragTabs([...tabs]);
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newTabs = [...tabs];
    const [removed] = newTabs.splice(draggedIndex, 1);
    newTabs.splice(index, 0, removed);
    setDragTabs(newTabs);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    if (draggedIndex === null) return;

    const orders = tabs.map((tab, index) => ({ id: tab.id, order: index }));

    startTransition(async () => {
      await post("/internal/client-tabs/reorder", { orders });
      invalidateTabs();
    });

    setDragTabs(null);
    setDraggedIndex(null);
  };

  // Count fields for a tab
  const getFieldsForTab = (tabId: string | null) => {
    return fields.filter((f) => f.tabId === tabId);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">{t.clientTabsDescription}</p>
        <Button onClick={handleCreate}>{t.addTab}</Button>
      </div>
      <Table variant="data">
        <TableHeader>
          <TableRow>
            <TableHead className="w-12" />
            <TableHead>{t.name}</TableHead>
            <TableHead>{t.fields}</TableHead>
            <TableHead className="text-right">{t.actions}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* Default tab - always first, not editable */}
          <TableRow>
            <TableCell />
            <TableCell className="font-medium">{t.defaultTab}</TableCell>
            <TableCell>
              <Badge variant="secondary">
                {getFieldsForTab(null).length} {t.fields.toLowerCase()}
              </Badge>
            </TableCell>
            <TableCell className="text-right">
              <span className="text-xs text-muted-foreground">{t.systemTab}</span>
            </TableCell>
          </TableRow>
          {/* Custom tabs */}
          {tabs.map((tab, index) => {
            const tabFields = getFieldsForTab(tab.id);
            return (
              <TableRow
                key={tab.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className="cursor-move"
              >
                <TableCell>
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                </TableCell>
                <TableCell className="font-medium">{localize(tab.nameI18n)}</TableCell>
                <TableCell>
                  <Badge variant="secondary">
                    {tabFields.length} {t.fields.toLowerCase()}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(tab)}
                      disabled={isPending}
                      tooltip={t.edit}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(tab.id, localize(tab.nameI18n))}
                      disabled={isPending}
                      tooltip={t.delete}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Tab Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTab ? t.editTab : t.addNewTab}</DialogTitle>
            <DialogDescription>
              {editingTab ? t.updateTabDescription : t.createTabDescription}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <LocalizedInput
                  id="tabName"
                  label={t.tabName}
                  value={tabName}
                  i18nValue={nameI18n}
                  onChange={setTabName}
                  onI18nChange={setNameI18n}
                  supportedLanguages={supportedLanguages}
                  placeholder={t.tabNamePlaceholder}
                  required
                />
              </div>
            </div>

            <DialogFooter>
              <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
              <Button type="submit" loading={isPending}>
                {editingTab ? t.save : t.addTab}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog />
    </div>
  );
}
